import { Box, Typography } from '@mui/material';

import { useIntakeStyle } from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import { radius } from '@karehero/llama/theme/radius';

// One message in the intake transcript. A copy of north-star's ChatBubble so
// the two can differ: this one reads at body1, because the chat is where the
// questions are asked and 14px was too small to read them at.
//
// The reply sits on coral.400 with floral white text. Loft runs the re-cut Figma
// ramp (palettePreview.ts), so the steps are not llama's: 400 is #F67A48 and 800
// is already #622B15.
//
// No replay button: nothing here is spoken aloud, so there is nothing to hear
// again.
/**
 * Two marks for a message that has to be scanned, Keni's only: **this** for a
 * heading, at the subtitle weight, and ~this~ for a label, in neutral 600, so
 * the eye goes to the value after it. Nothing else is parsed.
 */
const marked = (text: string) =>
  text.split(/(\*\*[^*]+\*\*|~[^~]+~)/).map((part, i) =>
    part.startsWith('**') ? (
      <Typography key={i} component='span' variant='subtitle1'>
        {part.slice(2, -2)}
      </Typography>
    ) : part.startsWith('~') ? (
      <Box key={i} component='span' sx={{ color: 'custom.neutral.600' }}>
        {part.slice(1, -1)}
      </Box>
    ) : (
      part
    ),
  );

export type ChatBubbleProps = {
  from: 'keni' | 'user';
  text: string;
  /**
   * It did not send. Only the colour: FailedMessage wraps it with the alert and
   * the way to try again, because a voice note fails the same way and draws
   * nothing like this.
   *
   * error.main is MUI's red, since llama overrides no semantic palette. Next to
   * the coral a sent message uses, that reads as wrong rather than as a variant.
   */
  failed?: boolean;
  /** A reply that can be tapped, to change the answer it gave. */
  onClick?: () => void;
};

const ChatBubble = ({ from, text, failed, onClick }: ChatBubbleProps) => {
  const { reply, replyInk } = useIntakeStyle();
  const isKeni = from === 'keni';

  return (
    <Box
      onClick={onClick}
      sx={{
        // A failed message is positioned by its wrapper, not by itself.
        ...(failed ? {} : { alignSelf: isKeni ? 'flex-start' : 'flex-end' }),
        maxWidth: failed ? '100%' : '82%',
        ...(onClick ? { cursor: 'pointer' } : {}),
      }}
    >
      <Box
        sx={{
          maxWidth: '100%',
          px: 2,
          py: 1.25,
          bgcolor: failed ? 'error.main' : isKeni ? 'background.paper' : reply,
          border: isKeni && !failed ? 1 : 0,
          borderColor: 'custom.neutral.200',
          borderRadius: `${radius.lg}px`,
          borderBottomLeftRadius: isKeni ? '4px' : '16px',
          borderBottomRightRadius: isKeni ? '16px' : '4px',
        }}
      >
        <Typography
          sx={{
            color: failed
              ? 'custom.neutral.50'
              : isKeni
                ? 'text.primary'
                : replyInk,
            // Copy can break its own lines. Validation reads like a form's when
            // the thing to do is on a line of its own, and there was no way to
            // say that in a bubble that collapsed every newline.
            whiteSpace: 'pre-line',
          }}
          variant='body1'
        >
          {isKeni ? marked(text) : text}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatBubble;
